import React, { useEffect, useRef, useState } from 'react';
import { Camera, ImagePlus, X, Loader2, Send, ClipboardCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Checkbox } from '../../ui/checkbox';
import { Skeleton } from '../../ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../ui/dialog';
import { easerGet, easerPost, easerPut, errorMessage, compressImage, fmtDate, fmtTime } from '../../../lib/easerApi';
import { StarRating, fmtAvg } from './qcShared';

const MAX_PHOTOS = 8;

/**
 * Création (intervention) ou modification (existing) d'un contrôle qualité par un manager :
 * étoiles par critère (grille admin), compte rendu, photos, option « mettre à disposition du client ».
 */
export function QualityControlDialog({ open, intervention, existing = null, onClose, onDone, onUnauthorized }) {
  const [criteria, setCriteria] = useState(null);
  const [scores, setScores] = useState({});
  const [report, setReport] = useState('');
  const [photos, setPhotos] = useState([]); // nouvelles photos {data, caption}
  const [removed, setRemoved] = useState([]); // index de photos existantes retirées
  const [share, setShare] = useState(false);
  const [saving, setSaving] = useState(false);
  const [adding, setAdding] = useState(false);
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setPhotos([]); setRemoved([]);
    setReport(existing?.report || '');
    setShare(!!existing?.share_with_client);
    setScores(existing ? Object.fromEntries((existing.criteria || []).map((c) => [c.id, c.stars])) : {});
    setCriteria(null);
    easerGet('/api/easer/qualite/criteres').then((r) => setCriteria(r.data.items || [])).catch((err) => {
      if (err?.response?.status === 401) return onUnauthorized();
      toast.error(errorMessage(err, 'Grille des critères indisponible'));
      setCriteria([]);
    });
  }, [open, existing, onUnauthorized]);

  const addFiles = async (files) => {
    const keptExisting = (existing?.photos || []).filter((p) => !removed.includes(p.index)).length;
    const list = Array.from(files || []).slice(0, Math.max(0, MAX_PHOTOS - keptExisting - photos.length));
    if (!list.length) return;
    setAdding(true);
    try {
      const next = [];
      for (const f of list) {
        try { next.push({ data: await compressImage(f), caption: '' }); } catch { toast.error(`Photo illisible : ${f.name}`); }
      }
      setPhotos((p) => [...p, ...next]);
    } finally { setAdding(false); }
  };

  const rated = Object.values(scores).filter(Boolean);
  const average = rated.length ? rated.reduce((a, b) => a + b, 0) / rated.length : null;
  const keptExisting = (existing?.photos || []).filter((p) => !removed.includes(p.index));
  const totalPhotos = keptExisting.length + photos.length;

  const submit = async () => {
    if (rated.length === 0) return toast.error('Attribuez au moins une note à un critère');
    setSaving(true);
    try {
      let res;
      if (existing) {
        res = await easerPut(`/api/easer/qualite/${existing.id}`, { scores, report, photos_add: photos, photos_remove: removed, share_with_client: share });
      } else {
        res = await easerPost(`/api/easer/events/${intervention.event_id}/qualite`, { scores, report, photos, share_with_client: share });
      }
      if (res.data.warning) toast.warning(res.data.warning, { duration: 8000 });
      else toast.success(existing ? 'Contrôle qualité mis à jour' : `Contrôle qualité enregistré${share ? ' et partagé avec le client' : ''}`);
      onDone(res.data.control);
    } catch (err) {
      if (err?.response?.status === 401) return onUnauthorized();
      toast.error(errorMessage(err, "Impossible d'enregistrer le contrôle"));
    } finally { setSaving(false); }
  };

  const label = existing?.label || intervention?.label;
  const easers = existing?.easer_names || intervention?.easer_names || [];
  const when = existing?.event_date || intervention?.start;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !saving) onClose(); }}>
      <DialogContent className="max-w-md w-[95vw] max-h-[92vh] overflow-y-auto rounded-2xl" data-testid="qc-dialog">
        <DialogHeader className="text-left">
          <DialogTitle className="font-['Space_Grotesk'] flex items-center gap-2" data-testid="qc-dialog-title"><ClipboardCheck className="h-5 w-5 text-[hsl(var(--primary))]" /> {existing ? 'Modifier le contrôle qualité' : 'Contrôle qualité'}</DialogTitle>
          <DialogDescription asChild>
            <div className="text-sm">
              <p className="font-medium text-foreground">{label}{(existing?.client_name || intervention?.client_name) ? ` · ${existing?.client_name || intervention?.client_name}` : ''}</p>
              <p>{when ? `${fmtDate(when, { weekday: 'short', day: 'numeric', month: 'short' })} ${fmtTime(when)}` : ''}{easers.length ? ` · ${easers.join(', ')}` : ''}</p>
            </div>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <Label>Évaluation par critère</Label>
              <span className="text-sm font-semibold" data-testid="qc-dialog-average">{average ? `${fmtAvg(average)} / 5` : '—'}</span>
            </div>
            {criteria === null ? <Skeleton className="h-40 rounded-xl" /> : criteria.length === 0 ? (
              <p className="text-sm text-destructive">Aucun critère actif : demandez à l'agence de configurer la grille dans le back-office.</p>
            ) : (
              <ul className="divide-y divide-border rounded-xl border border-border" data-testid="qc-dialog-criteria">
                {criteria.map((c) => (
                  <li key={c.id} className="flex items-center justify-between gap-2 px-3 py-1">
                    <div className="min-w-0">
                      <p className="text-sm font-medium">{c.label}</p>
                      {c.description && <p className="text-xs text-muted-foreground">{c.description}</p>}
                    </div>
                    <StarRating value={scores[c.id] || 0} onChange={(n) => setScores((s) => ({ ...s, [c.id]: s[c.id] === n ? 0 : n }))} size="h-6 w-6" testId={`qc-stars-${c.id}`} label={c.label} />
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="qc-report">Compte rendu</Label>
            <Textarea id="qc-report" value={report} onChange={(e) => setReport(e.target.value)} rows={4} placeholder="Points forts, points à améliorer, consignes pour la prochaine fois…" data-testid="qc-report-input" />
          </div>

          <div className="space-y-2">
            <Label>Photos ({totalPhotos}/{MAX_PHOTOS})</Label>
            <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} data-testid="qc-camera-input" />
            <input ref={galleryRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} data-testid="qc-gallery-input" />
            <div className="grid grid-cols-2 gap-2">
              <Button type="button" variant="outline" className="h-11" disabled={adding || totalPhotos >= MAX_PHOTOS} onClick={() => cameraRef.current?.click()} data-testid="qc-take-photo">
                {adding ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Camera className="h-4 w-4 mr-1" />} Prendre une photo
              </Button>
              <Button type="button" variant="outline" className="h-11" disabled={adding || totalPhotos >= MAX_PHOTOS} onClick={() => galleryRef.current?.click()} data-testid="qc-pick-photo">
                <ImagePlus className="h-4 w-4 mr-1" /> Galerie
              </Button>
            </div>
            {(keptExisting.length > 0 || photos.length > 0) && (
              <ul className="grid grid-cols-3 gap-2" data-testid="qc-photo-list">
                {keptExisting.map((p) => (
                  <li key={`e-${p.index}`} className="relative rounded-lg border border-border overflow-hidden">
                    <img src={p.thumb} alt={p.caption || 'Photo'} className="h-24 w-full object-cover" />
                    <button type="button" onClick={() => setRemoved((r) => [...r, p.index])} aria-label="Retirer" className="absolute top-1 right-1 h-7 w-7 rounded-full bg-black/60 text-white flex items-center justify-center" data-testid="qc-photo-remove-existing"><X className="h-4 w-4" /></button>
                  </li>
                ))}
                {photos.map((p, i) => (
                  <li key={`n-${i}`} className="relative rounded-lg border border-border overflow-hidden">
                    <img src={p.data} alt={`Photo ${i + 1}`} className="h-24 w-full object-cover" />
                    <button type="button" onClick={() => setPhotos((ps) => ps.filter((_, idx) => idx !== i))} aria-label="Retirer" className="absolute top-1 right-1 h-7 w-7 rounded-full bg-black/60 text-white flex items-center justify-center" data-testid="qc-photo-remove"><X className="h-4 w-4" /></button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <label className="flex items-start gap-3 rounded-xl border border-border p-3 cursor-pointer hover:bg-secondary/50" data-testid="qc-share-label">
            <Checkbox checked={share} onCheckedChange={(v) => setShare(!!v)} className="mt-0.5" data-testid="qc-share-checkbox" />
            <span className="text-sm">
              <span className="font-medium">Mettre à disposition du client</span>
              <span className="block text-xs text-muted-foreground">Le contrôle (notes, compte rendu, photos) apparaît dans son Espace Client et un e-mail l'en informe. La réponse de l'Easer reste interne.</span>
            </span>
          </label>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="qc-cancel">Annuler</Button>
          <Button onClick={submit} disabled={saving || adding || !criteria?.length} className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white" data-testid="qc-submit">
            {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />} {existing ? 'Enregistrer' : 'Valider le contrôle'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
